import { IconButton, Menu, MenuItem } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { FAIcon } from '@packages/ui-components';

type ActionButtonProps = {
  icon: React.ReactNode | string;
  label: string;
  onClick: () => void;
};

function ActionButton({ icon, label, onClick }: ActionButtonProps) {
  return (
    <MenuItem
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{ display: 'flex', alignItems: 'center', gap: '12px', height: '40px' }}
    >
      {typeof icon === 'string' ? <i className={icon} style={{ fontSize: '16px', color: '#667085' }} /> : icon}
      <Typography style={{ color: 'var(--Gray-700, #344054)', fontFamily: 'Inter', fontSize: '14px', fontStyle: 'normal', fontWeight: 500, lineHeight: '20px' }}>
        {label}
      </Typography>
    </MenuItem>
  );
}

type Props = {
  isCompleted: boolean;
  onDelete?: () => void;
  onEdit?: () => void;
  onStatusChange?: (isCompleted: boolean) => void;
  onMoveToBacklog?: () => void;
};

export function NoteActions({ onDelete, onEdit, onStatusChange, isCompleted, onMoveToBacklog }: Props) {
  const anchorElRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <IconButton
        ref={anchorElRef}
        sx={{ width: '22px', height: '22px' }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <FAIcon icon="faEllipsisVertical" size={16} color="#667085" />
      </IconButton>
      <Menu
        anchorEl={anchorElRef.current}
        open={open}
        elevation={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={(e: any) => {
          e.stopPropagation();
          setOpen(false);
        }}
        sx={{
          '.MuiPaper-root': {
            background: 'white',
            borderRadius: '8px',
            border: '1px solid var(--Gray-100, #F2F4F7)',
            boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
          },
        }}
        MenuListProps={{
          sx: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        }}
      >
        {isCompleted ? (
          <ActionButton
            label="Mark as incomplete"
            icon={<FAIcon icon="faArrowRotateLeft" size={16} color="#FB6514" />}
            onClick={() => {
              onStatusChange?.(false);
              setOpen(false);
            }}
          />
        ) : (
          <ActionButton
            label="Mark as complete"
            icon={<Check size={16} style={{ color: '#12B76A' }} />}
            onClick={() => {
              onStatusChange?.(true);
              setOpen(false);
            }}
          />
        )}

        {onEdit ? (
          <ActionButton
            label="Edit"
            icon={<FAIcon icon="faPenToSquare" size={16} color="#667085" />}
            onClick={() => {
              onEdit?.();
              setOpen(false);
            }}
          />
        ) : null}

        {onDelete ? (
          <ActionButton
            label="Delete"
            icon={<FAIcon icon="faTrashCan" size={16} color="#667085" />}
            onClick={() => {
              onDelete?.();
              setOpen(false);
            }}
          />
        ) : null}

        {onMoveToBacklog ? (
          <ActionButton
            label="Back to backlog"
            icon={<FAIcon icon="faArrowRightFromBracket" size={16} color="#667085" />}
            onClick={() => {
              onMoveToBacklog();
              setOpen(false);
            }}
          />
        ) : null}
      </Menu>
    </>
  );
}
